import { Component, effect, EventEmitter, inject, Output, signal, WritableSignal } from '@angular/core';
import { Auth, getIdToken } from '@angular/fire/auth';
import { FormControl, FormGroup, FormRecord, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { IamRolesComponentState } from '../shared/state-interface';
import { StateManager } from '../shared/state-manager.class';
import { IamRole } from './iam-role.model';
import { IamService } from './iam.service';

@Component({
    selector: 'app-iam-roles-component',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatCheckboxModule
    ],
    templateUrl: './iam-roles.component.html',
    styleUrl: './iam-roles.component.css'
})
export class IamRolesComponent {

    @Output() messageEvent = new EventEmitter<string>();

    private auth: Auth = inject(Auth);
    private stateManager: StateManager = inject(StateManager);
    private iamService: IamService = inject(IamService);

    private static _iamRolesError = "Get IAM Roles error:";

    protected _iamRolesForm: FormRecord = new FormRecord({});

    private _iamRoles: WritableSignal<IamRole[]> = signal([]);
    private _formIsReady: WritableSignal<boolean> = signal(false);
    private _userRoles: WritableSignal<string[]> = signal(["none"]);

    private _userType: string = "";

    public constructor() {
        const effectRef = effect(() => {
            if (this._iamRoles().length > 0 && this._userRoles()[0] != "none") {
                for (const iamRole of this._iamRoles()) {
                    this._iamRolesForm.addControl(iamRole.long_role_name,
                        new FormControl(this._userRoles().indexOf(iamRole.long_role_name) > -1));
                }
                this._formIsReady.set(true);
                // Only need/expect to run this effect one time
                // once all conditions are met. Not sure that it
                // is necessary to destroy but doing so to be on
                // the safe side relative to avoiding memory leak.
                effectRef.destroy();
            }
        },{ allowSignalWrites: true, manualCleanup: true });
    }

    protected get iamRolesForm(): FormGroup {
        return this._iamRolesForm;
    }

    protected set iamRolesForm(value: FormGroup) {
        this._iamRolesForm = this.iamRolesForm;
    }

    public getState(): IamRolesComponentState {
        const state: IamRolesComponentState = {} as IamRolesComponentState
        state.iamRolesFormControls = this.iamRolesForm.controls;
        state.userType = this._userType;
        state.userRoles = this._userRoles();
        return state;
    }

    public restoreState(state: IamRolesComponentState) {
        this.iamRolesForm.controls = state.iamRolesFormControls;
        this._userType = state.userType;
        this._iamRoles.set(this.stateManager.getIamRoles(this._userType)!);
        this._userRoles.set(state.userRoles);
    }

    public init(userType: string) {
        this._userType = userType;

        if (this._iamRoles().length == 0) {
            const iamRoles = this.stateManager.getIamRoles(this._userType);

            if (iamRoles == undefined) {
                this.loadIamRoles(userType);
            } else {
                this._iamRoles.set(iamRoles);
            }
        }
    }

    public set userRoles(userRoles: string[]) {
        this._userRoles.set(userRoles);
    }

    protected get formIsReady(): boolean {
        return this._formIsReady();
    }

    protected get iamRoles(): IamRole[] {
        return this._iamRoles();
    }

    public get assignedRoles(): string[] {
        const assignedRoles: string[] = [];

        for (const iamRole of this._iamRoles()!) {
            const control: any = this.iamRolesForm.controls[iamRole.long_role_name];
            if (control.value) {
                assignedRoles.push(iamRole.long_role_name);
            }
        }

        return assignedRoles;
    }

    public async loadIamRoles(userType: string) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.iamService.getIamRoles(await getIdToken(currentUser), userType);

            observable.subscribe(response => {
                // For testing to validate proper rendering of form fields
                // when retrieving data over slow internet connections.
                //setTimeout(() => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["get-iam-roles"] == "success") {
                        // Convert generic JSON object to type of IamRole.
                        const iamRoles = IamRole.toIamRoles(json["iam-roles"]);
                        this._iamRoles.set(iamRoles);
                        this.stateManager.setIamRoles(userType, this._iamRoles());
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${IamRolesComponent._iamRolesError} ${(<Error>error).message}`;
                    console.error(message);
                    this.messageEvent.emit(message);
                }
                //}, 5000);
            })
        }
    }
}
